import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';
import SystemHealth from './SystemHealth';
import AlertsPanel from './AlertsPanel';
import LiveDataFeed from './LiveDataFeed';
import DiscrepancyTracker from './DiscrepancyTracker';

const Container = styled.div`
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const SystemStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.status) {
      case 'healthy': return '#d4edda';
      case 'warning': return '#fff3cd';
      case 'critical': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'healthy': return '#155724';
      case 'warning': return '#856404';
      case 'critical': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'healthy': return '#28a745';
      case 'warning': return '#ffc107';
      case 'critical': return '#dc3545';
      default: return '#6c757d';
    }
  }};
  animation: ${props => props.status !== 'healthy' ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #28a745;
  font-weight: 500;
  font-size: 14px;
`;

const LiveDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #28a745;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MonitoringCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: ${props => props.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CardContent = styled.div`
  padding: 25px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const MetricCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const MetricIcon = styled.div`
  font-size: 32px;
  margin-bottom: 15px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const MetricTrend = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${props => {
    switch (props.trend) {
      case 'up': return '#28a745';
      case 'down': return '#dc3545';
      default: return '#6c757d';
    }
  }};
`;

const AlertBanner = styled.div`
  background: ${props => {
    switch (props.severity) {
      case 'high': return 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)';
      case 'medium': return 'linear-gradient(135deg, #feca57 0%, #ff9ff3 100%)';
      case 'low': return 'linear-gradient(135deg, #48dbfb 0%, #0abde3 100%)';
      default: return 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
    }
  }};
  color: white;
  padding: 15px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const ControlPanel = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ControlButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &.active {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const RefreshIndicator = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 5px;
`;

const RealTimeMonitoring = () => {
  const { syncStatus, inventoryItems, totalValue } = useInventory();
  const [systemHealth, setSystemHealth] = useState('healthy');
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [metrics, setMetrics] = useState({
    cpuUsage: 45,
    memoryUsage: 67,
    networkLatency: 23,
    transactionThroughput: 1250,
    blockchainHeight: 2847562,
    connectedPeers: 143
  });

  // Simulate real-time updates
  useEffect(() => {
    if (!isLive) return;

    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        cpuUsage: Math.max(0, Math.min(100, prev.cpuUsage + (Math.random() - 0.5) * 10)),
        memoryUsage: Math.max(0, Math.min(100, prev.memoryUsage + (Math.random() - 0.5) * 5)),
        networkLatency: Math.max(0, prev.networkLatency + (Math.random() - 0.5) * 10),
        transactionThroughput: Math.max(0, prev.transactionThroughput + Math.floor((Math.random() - 0.5) * 200)),
        blockchainHeight: prev.blockchainHeight + Math.floor(Math.random() * 3),
        connectedPeers: Math.max(0, prev.connectedPeers + Math.floor((Math.random() - 0.5) * 10))
      }));

      setLastUpdate(new Date());

      // Generate random alerts
      if (Math.random() < 0.1) { // 10% chance
        const alertTypes = [
          { type: 'sync', message: 'Blockchain sync lag detected', severity: 'medium' },
          { type: 'inventory', message: 'Inventory discrepancy detected', severity: 'high' },
          { type: 'network', message: 'Network latency above threshold', severity: 'low' },
          { type: 'storage', message: 'Storage capacity warning', severity: 'medium' }
        ];
        
        const newAlert = {
          id: Date.now(),
          ...alertTypes[Math.floor(Math.random() * alertTypes.length)],
          timestamp: new Date()
        };

        setActiveAlerts(prev => [newAlert, ...prev.slice(0, 4)]);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isLive]);

  // Update system health based on metrics
  useEffect(() => {
    const { cpuUsage, memoryUsage, networkLatency } = metrics;
    
    if (cpuUsage > 90 || memoryUsage > 90 || networkLatency > 100) {
      setSystemHealth('critical');
    } else if (cpuUsage > 70 || memoryUsage > 80 || networkLatency > 50) {
      setSystemHealth('warning');
    } else {
      setSystemHealth('healthy');
    }
  }, [metrics]);

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.floor(value));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return '💚';
      case 'warning': return '⚠️';
      case 'critical': return '🚨';
      default: return '❓';
    }
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return '📈';
    if (current < previous) return '📉';
    return '➡️';
  };

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>Real-Time Monitoring</Title>
        <StatusIndicator>
          <SystemStatus status={systemHealth}>
            <StatusDot status={systemHealth} />
            {getHealthIcon(systemHealth)} System {systemHealth}
          </SystemStatus>
          <LiveIndicator>
            <LiveDot />
            {isLive ? 'LIVE' : 'PAUSED'}
          </LiveIndicator>
        </StatusIndicator>
      </Header>

      {/* Active Alerts */}
      {activeAlerts.slice(0, 1).map(alert => (
        <AlertBanner key={alert.id} severity={alert.severity}>
          <span style={{ fontSize: '20px' }}>
            {alert.severity === 'high' && '🚨'}
            {alert.severity === 'medium' && '⚠️'}
            {alert.severity === 'low' && 'ℹ️'}
          </span>
          <div>
            <strong>{alert.type.toUpperCase()} ALERT:</strong> {alert.message}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.8 }}>
            {alert.timestamp.toLocaleTimeString()}
          </div>
        </AlertBanner>
      ))}

      {/* Key Metrics Grid */}
      <MetricsGrid>
        <MetricCard>
          <MetricIcon>🖥️</MetricIcon>
          <MetricValue>{metrics.cpuUsage.toFixed(1)}%</MetricValue>
          <MetricLabel>CPU Usage</MetricLabel>
          <MetricTrend trend={metrics.cpuUsage > 50 ? 'up' : 'down'}>
            {getTrendIcon(metrics.cpuUsage, 45)} Real-time
          </MetricTrend>
        </MetricCard>

        <MetricCard>
          <MetricIcon>💾</MetricIcon>
          <MetricValue>{metrics.memoryUsage.toFixed(1)}%</MetricValue>
          <MetricLabel>Memory Usage</MetricLabel>
          <MetricTrend trend={metrics.memoryUsage > 60 ? 'up' : 'down'}>
            {getTrendIcon(metrics.memoryUsage, 67)} Real-time
          </MetricTrend>
        </MetricCard>

        <MetricCard>
          <MetricIcon>🌐</MetricIcon>
          <MetricValue>{metrics.networkLatency.toFixed(0)}ms</MetricValue>
          <MetricLabel>Network Latency</MetricLabel>
          <MetricTrend trend={metrics.networkLatency > 30 ? 'up' : 'down'}>
            {getTrendIcon(metrics.networkLatency, 23)} Real-time
          </MetricTrend>
        </MetricCard>

        <MetricCard>
          <MetricIcon>⚡</MetricIcon>
          <MetricValue>{formatNumber(metrics.transactionThroughput)}</MetricValue>
          <MetricLabel>TX/Hour</MetricLabel>
          <MetricTrend trend="up">
            📈 +12.5%
          </MetricTrend>
        </MetricCard>

        <MetricCard>
          <MetricIcon>🔗</MetricIcon>
          <MetricValue>{formatNumber(metrics.blockchainHeight)}</MetricValue>
          <MetricLabel>Block Height</MetricLabel>
          <MetricTrend trend="up">
            📈 Synced
          </MetricTrend>
        </MetricCard>

        <MetricCard>
          <MetricIcon>👥</MetricIcon>
          <MetricValue>{metrics.connectedPeers}</MetricValue>
          <MetricLabel>Connected Peers</MetricLabel>
          <MetricTrend trend="stable">
            ➡️ Stable
          </MetricTrend>
        </MetricCard>
      </MetricsGrid>

      {/* Main Dashboard */}
      <DashboardGrid>
        <MainContent>
          {/* System Health */}
          <MonitoringCard>
            <CardHeader>
              <CardTitle>
                🏥 System Health Monitor
              </CardTitle>
              <ControlPanel>
                <RefreshIndicator>
                  🔄 Last update: {lastUpdate.toLocaleTimeString()}
                </RefreshIndicator>
                <ControlButton 
                  className={isLive ? 'active' : ''}
                  onClick={() => setIsLive(!isLive)}
                >
                  {isLive ? '⏸️ Pause' : '▶️ Resume'}
                </ControlButton>
              </ControlPanel>
            </CardHeader>
            <CardContent>
              <SystemHealth metrics={metrics} />
            </CardContent>
          </MonitoringCard>

          {/* Discrepancy Tracker */}
          <MonitoringCard>
            <CardHeader gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
              <CardTitle>
                🔍 Inventory vs Blockchain Tracker
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DiscrepancyTracker />
            </CardContent>
          </MonitoringCard>

          {/* Live Data Feed */}
          <MonitoringCard>
            <CardHeader gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
              <CardTitle>
                📡 Live Data Feed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LiveDataFeed isLive={isLive} />
            </CardContent>
          </MonitoringCard>
        </MainContent>

        <Sidebar>
          {/* Alerts Panel */}
          <MonitoringCard>
            <CardHeader gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
              <CardTitle>
                🚨 Active Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AlertsPanel alerts={activeAlerts} />
            </CardContent>
          </MonitoringCard>

          {/* Quick Stats */}
          <MonitoringCard>
            <CardHeader gradient="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)">
              <CardTitle>
                📊 Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                    Total Portfolio Value
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50' }}>
                    {formatCurrency(totalValue)}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                    Inventory Items
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50' }}>
                    {inventoryItems.length}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>
                    Sync Status
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: syncStatus.inSync ? '#28a745' : '#dc3545' 
                  }}>
                    {syncStatus.inSync ? '✅ In Sync' : '⚠️ Out of Sync'}
                  </div>
                </div>
              </div>
            </CardContent>
          </MonitoringCard>
        </Sidebar>
      </DashboardGrid>
    </Container>
  );
};

export default RealTimeMonitoring; 